import { groups, sum } from "d3";
import { LocationDetailLevel, TradeDirection } from "@atlas-int/data/types";
import { getTradeValueForWorldSelection } from "@atlas-int/data";
import { getLocationLevelFromStringLocationId, servicesPartnersGroupId } from "@atlas-int/data";
import { LocationLevel } from "@atlas-int/data/graphql";
import {
  createLocationHierarchyIndex,
  enrichLocationWithHierarchyFast,
} from "@atlas-int/data";
import type { TradeDataItem, EnrichedTradeDataItem } from "@atlas-int/data/types";
import type { RegionMetadata, CountryMetadata } from "@atlas-int/data/types";


type GetValueFn = (exportValue: number, importValue: number) => number;

// --- Shared grouping helpers ---

/** Group items by a hierarchy level (region or subregion) and aggregate values */
const groupByHierarchyLevel = (
  items: EnrichedTradeDataItem[],
  levelAccessor: (d: EnrichedTradeDataItem) => { id: string; nameEn: string },
  getValue: GetValueFn,
  baseFields: { year: number; countryId?: string; locationLevel?: string; partnerLevel?: string },
): EnrichedTradeDataItem[] => {
  return groups(items, (d) => levelAccessor(d).id).map(
    ([groupId, groupItems]) => {
      const sumExportValue = sum(groupItems, (v) => v.exportValue);
      const sumImportValue = sum(groupItems, (v) => v.importValue);
      return {
        ...baseFields,
        id: groupId,
        partnerCountryId: groupId,
        groupName: levelAccessor(groupItems[0]).nameEn,
        exportValue: sumExportValue,
        importValue: sumImportValue,
        topLevelParent: groupItems[0].topLevelParent,
        hybrid_level_1: groupItems[0].hybrid_level_1,
        hybrid_level_2: groupItems[0].hybrid_level_2,
      } as EnrichedTradeDataItem;
    },
  );
};

/** Create a single aggregated services partner group */
const createServicesGroup = (
  servicesPartners: EnrichedTradeDataItem[],
  getValue: GetValueFn,
  baseFields: { year: number; countryId?: string; locationLevel?: string; partnerLevel?: string },
): EnrichedTradeDataItem | undefined => {
  if (servicesPartners.length === 0) return undefined;
  const sumExportValue = sum(servicesPartners, (v) => v.exportValue);
  const sumImportValue = sum(servicesPartners, (v) => v.importValue);
  return {
    ...baseFields,
    id: servicesPartnersGroupId,
    partnerCountryId: servicesPartnersGroupId,
    groupName: "Services Partners",
    exportValue: sumExportValue,
    importValue: sumImportValue,
    topLevelParent: "root",
    hybrid_level_1: {
      id: servicesPartnersGroupId,
      nameEn: "Services Partners",
    },
    hybrid_level_2: {
      id: servicesPartnersGroupId,
      nameEn: "Services Partners",
    },
  } as EnrichedTradeDataItem;
};

/** Apply detail-level grouping: country (no grouping), subregion, or region */
const applyDetailLevelGrouping = (
  dataWithParents: EnrichedTradeDataItem[],
  locationLevel: LocationDetailLevel,
  getValue: GetValueFn,
  baseFields: { year: number; countryId?: string; locationLevel?: string; partnerLevel?: string },
): EnrichedTradeDataItem[] => {
  if (locationLevel === LocationDetailLevel.country) {
    return dataWithParents;
  }

  const servicesPartners = dataWithParents.filter(
    (d) => d.topLevelParent === servicesPartnersGroupId,
  );
  const nonServicesPartners = dataWithParents.filter(
    (d) => d.topLevelParent !== servicesPartnersGroupId,
  );

  const levelAccessor =
    locationLevel === LocationDetailLevel.region
      ? (d: EnrichedTradeDataItem) => d.hybrid_level_1!
      : (d: EnrichedTradeDataItem) => d.hybrid_level_2!;

  const grouped = groupByHierarchyLevel(
    nonServicesPartners,
    levelAccessor,
    getValue,
    baseFields,
  );

  // For region level, items become direct children of root
  if (locationLevel === LocationDetailLevel.region) {
    grouped.forEach((item) => {
      item.topLevelParent = "root";
    });
  }

  const servicesGroup = createServicesGroup(
    servicesPartners,
    getValue,
    baseFields,
  );
  return servicesGroup ? [...grouped, servicesGroup] : grouped;
};

// --- Main transform ---

interface TransformLocationsParams {
  data: TradeDataItem[];
  year: number;
  location: string;
  locationDetailLevel: LocationDetailLevel;
  regions: RegionMetadata[];
  subregions: RegionMetadata[];
  countries: CountryMetadata[];
  tradeDirection: TradeDirection;
  isWorldToWorldQuery?: boolean;
}

const transformLocations = ({
  data,
  year,
  location,
  locationDetailLevel,
  regions,
  subregions,
  countries,
  tradeDirection,
  isWorldToWorldQuery = false,
}: TransformLocationsParams): EnrichedTradeDataItem[] | undefined => {
  const getValue: GetValueFn = (exportValue, importValue) => {
    return getTradeValueForWorldSelection({
      exportValue,
      importValue,
      tradeDirection,
      isWorldToWorldQuery,
    });
  };

  const topLevelParents = new Set(
    regions.map((region: RegionMetadata) => region.groupId),
  );

  const locationLevel = getLocationLevelFromStringLocationId(location);

  if (data.length === 0) {
    return undefined;
  }

  // Step 5: Pre-compute location hierarchy index for O(1) lookups
  const locationIndex = createLocationHierarchyIndex(
    regions,
    subregions,
    countries,
  );

  /** Enrich a list of grouped trade data with location hierarchy info */
  const enrichWithHierarchy = (
    groupedData: Array<{
      partnerCountryId: number;
      id: number | string;
      locationLevel?: string;
      partnerLevel?: string;
      year: number;
      exportValue: number;
      importValue: number;
      productsData?: TradeDataItem[];
      value?: number;
    }>,
  ): EnrichedTradeDataItem[] => {
    return groupedData.map((d) => {
      const locationHierarchy = enrichLocationWithHierarchyFast(
        d.partnerCountryId,
        locationIndex,
      );
      return {
        ...d,
        id: d.partnerCountryId,
        topLevelParent: locationHierarchy.topLevelParent,
        nameEn: locationHierarchy.nameEn,
        hybrid_level_1: locationHierarchy.hybrid_level_1,
        hybrid_level_2: locationHierarchy.hybrid_level_2,
        countryIso3Code: locationHierarchy.countryIso3Code,
      } as EnrichedTradeDataItem;
    });
  };

  /** Build the final output array with root + parent nodes + data */
  const buildResult = (
    groupedData: EnrichedTradeDataItem[],
    includeTopLevelParents: boolean = true,
  ) => {
    const rootObject = { id: "root", topLevelParent: undefined } as EnrichedTradeDataItem;
    const topLevelParentsObjects = includeTopLevelParents
      ? [...topLevelParents.values()].map((t) => ({
          id: t,
          topLevelParent: "root",
        } as EnrichedTradeDataItem))
      : [];
    return [rootObject, ...topLevelParentsObjects, ...groupedData];
  };

  if (location) {
    if (locationLevel === LocationLevel.Country || locationLevel === LocationLevel.Group) {
      // Group raw data by partner country and sum values
      const groupedFilteredData = groups(
        data,
        (d: TradeDataItem) => d.partnerCountryId,
      ).map((d: [number, TradeDataItem[]]) => {
        const partnerCountryId = d[0];
        const partnerLevel = d[1][0].partnerLevel;
        const sumExportValue = sum(d[1], (v: TradeDataItem) => v.exportValue);
        const sumImportValue = sum(d[1], (v: TradeDataItem) => v.importValue);
        return {
          id: location,
          locationLevel: "country",
          partnerCountryId,
          partnerLevel,
          year: year,
          exportValue: sumExportValue,
          importValue: sumImportValue,
          productsData: d[1],
          value: getValue(sumExportValue, sumImportValue),
        };
      });

      const dataWithParents = enrichWithHierarchy(groupedFilteredData);

      const baseFields =
        locationLevel === LocationLevel.Country
          ? {
              year,
              countryId: location,
              locationLevel: "country",
              partnerLevel: LocationLevel.Group,
            }
          : {
              year,
              partnerLevel: LocationLevel.Group as string,
            };

      const groupedByLocationDetailLevel = applyDetailLevelGrouping(
        dataWithParents,
        locationDetailLevel,
        getValue,
        baseFields,
      );

      const skipTopLevelParents =
        locationLevel === LocationLevel.Group &&
        locationDetailLevel === LocationDetailLevel.region;

      return buildResult(groupedByLocationDetailLevel, !skipTopLevelParents);
    }
    return [];
  } else {
    // No location selected — show all countries grouped by region/subregion
    const dataWithParents = data.map((d: TradeDataItem) => {
      const countryId = d.countryId;
      const locationHierarchy = enrichLocationWithHierarchyFast(
        countryId,
        locationIndex,
      );
      return {
        ...d,
        id: d.countryId,
        topLevelParent: locationHierarchy.topLevelParent,
        nameEn: locationHierarchy.nameEn,
        hybrid_level_1: locationHierarchy.hybrid_level_1,
        hybrid_level_2: locationHierarchy.hybrid_level_2,
      } as EnrichedTradeDataItem;
    });

    const groupedByLocationDetailLevel = applyDetailLevelGrouping(
      dataWithParents,
      locationDetailLevel,
      getValue,
      { year },
    );

    const topLevelParentsObjects = [...topLevelParents.values()].map((t) => ({
      id: t,
      topLevelParent: "root",
    } as EnrichedTradeDataItem));
    topLevelParentsObjects.push({ id: "undisclosed", topLevelParent: "root" } as EnrichedTradeDataItem);

    const rootObject = { id: "root", topLevelParent: undefined } as EnrichedTradeDataItem;
    return [
      rootObject,
      ...topLevelParentsObjects,
      ...groupedByLocationDetailLevel,
    ];
  }
};

export default transformLocations;
